// Getting started with an integrator account: the steps, their state, and the examples to copy.
// No dependency on Nuxt here: the file can be tested with Node alone. Its texts live in lang/*.json, keyed by the codes
// below: `onboarding.phases.<key>` (title, text), `onboarding.steps.<key>` (title, eta), `onboarding.badges.<key>`,
// `onboarding.journey.<key>`, `nav.<key>`, `help.topics.general`.

export const PHASES = [{ key: "try" }, { key: "connect" }, { key: "live" }];

// `done` reads the milestones of GET /accounts/:id/onboarding ("this has already happened", never a current state)
// and the dashboard context: the account's subscription, the number of connected MCP applications and whether the
// person writes the code (`writesNoCode`, from the onboarding answers).
// Each step's title and `eta` (an order of magnitude, so that « Objectif en cours » says what it takes) are
// `onboarding.steps.<key>.title` and `.eta`; the account, always done, has no eta.
// `optional`: true, or a function of the context.
export const STEPS = [
  { key: "account", phase: "try", done: () => true },
  // Chosen at the end of the onboarding (/welcome), before the dashboard opens: already ticked when the list shows.
  { key: "offer", phase: "try", done: (m, ctx) => ctx.hasSubscription },
  // The developers get their own access, keys and steps included: nothing to forward by e-mail. Required for someone who
  // does not write the code (the next step is to bring in whoever will), a choice for the others.
  { key: "team", phase: "try", optional: (ctx) => !ctx.writesNoCode, done: (m) => m.teamInvited },
  { key: "testKey", phase: "try", done: (m) => m.testKey },
  { key: "testOrder", phase: "try", done: (m) => m.testOrder },
  { key: "lifecycle", phase: "try", done: (m) => m.testOrderShipped },
  { key: "webhookEndpoint", phase: "connect", done: (m) => m.webhookEndpoint },
  { key: "webhookDelivered", phase: "connect", done: (m) => m.webhookDelivered },
  { key: "mcp", phase: "connect", optional: true, done: (m, ctx) => ctx.mcpGrants > 0 },
  { key: "liveKey", phase: "live", done: (m) => m.liveKey },
  { key: "liveOrder", phase: "live", done: (m) => m.liveOrder }
];

// The achievements of the getting-started screen (`onboarding.badges.<key>`): one per big moment, tied to the step
// that unlocks it.
export const ACHIEVEMENTS = [
  { key: "testKey", icon: "i-heroicons-key" },
  { key: "testOrder", icon: "i-heroicons-cube" },
  { key: "lifecycle", icon: "i-heroicons-truck" },
  { key: "webhookDelivered", icon: "i-heroicons-bolt" },
  { key: "mcp", icon: "i-heroicons-cpu-chip" },
  { key: "liveOrder", icon: "i-heroicons-rocket-launch" }
];

// Without an API response, nothing is checked: the screen stays usable, it just does not know where you are.
export const buildSteps = (milestones, context = {}) => STEPS.map((step) => ({
  ...step,
  optional: typeof step.optional === "function" ? !!step.optional(context) : !!step.optional,
  done: !!step.done(milestones ?? {}, context)
}));

// An optional step counts neither in the total nor in "the next step".
export const progress = (steps) => {
  const required = steps.filter((step) => !step.optional);
  return { done: required.filter((step) => step.done).length, total: required.length };
};

export const nextStep = (steps) => steps.find((step) => !step.optional && !step.done) ?? null;

// Progress of each phase (optional steps excluded): the segmented bar and the phase summaries.
export const phaseProgress = (steps) => PHASES.map((phase) => {
  const required = steps.filter((step) => step.phase === phase.key && !step.optional);
  const done = required.filter((step) => step.done).length;
  return { key: phase.key, done, total: required.length, complete: required.length > 0 && done === required.length };
});

// What just happened between two reads: the checked steps, and the phases finished along the way.
export const progressDelta = (before, after) => {
  const was = new Set(before.filter((step) => step.done).map((step) => step.key));
  const phasesWere = new Map(phaseProgress(before).map((phase) => [phase.key, phase.complete]));
  return {
    steps: after.filter((step) => step.done && !was.has(step.key)),
    phases: phaseProgress(after).filter((phase) => phase.complete && !phasesWere.get(phase.key)).map((phase) => phase.key)
  };
};

const matches = (path, base) => path === base || path.startsWith(`${ base }/`);

// What a dashboard account must have done before reaching the dashboard, in the order of the onboarding: confirm its
// address (/verify-email), answer the questions and choose a plan (/welcome, its free trial counts). Without a plan,
// only the onboarding and signing out stay open (the owner's choice of 26/10/2026: nothing else until the person
// subscribes; the team follows up by hand with the answers). The API refuses keys and orders without a plan as well
// once `planRequiredForApi` is on (402 SUBSCRIPTION_REQUIRED).
export const OPEN_WITHOUT_PLAN = ["/welcome", "/logout"];

// Where to send someone who asks for `path`, or null when the page is theirs.
// `emailVerified === false` only: an API that predates the verification sends nothing, and must not lock anyone out.
// `isAdmin`: an administrator looking at a client account sees it as it is, without answering in the client's place.
export const onboardingRedirect = ({ emailVerified, hasPlan, isAdmin = false }, path) => {
  if (isAdmin) return null;
  if (emailVerified === false) return matches(path, "/verify-email") || matches(path, "/logout") ? null : "/verify-email";
  if (path === "/verify-email") return hasPlan ? "/dashboard" : "/welcome";
  if (hasPlan) return null;
  return OPEN_WITHOUT_PLAN.some((base) => matches(path, base)) ? null : "/welcome";
};

// ─── After the plan: the guided getting started, then the full dashboard ─────────────────────────────────────────────
//
// The second onboarding. Once /welcome has opened the plan, the dashboard guides the account through its integration: its
// home is the getting-started page and its navigation keeps the tools of the steps. The full dashboard opens when the
// first real order is placed (or every step is done), or as soon as the person chooses it (`dismissedAt`, stored for the
// whole account); the getting started stays a click away until it is finished.

// "none": no plan (an administrator looking at a client account); "guided"; "full"; null while unknown, until the API
// answers, for an account this browser has never seen. An API that fails keeps the last mode seen, or opens the full
// dashboard: nobody is held in a getting started whose progress cannot be read.
export const setupMode = ({ hasSubscription, loaded, failed = false, finished, dismissedAt, cached = null }) => {
  if (!hasSubscription) return "none";
  if (failed) return cached ?? "full";
  if (!loaded) return cached ?? null;
  return finished || dismissedAt ? "full" : "guided";
};

// The last mode seen for an account, so that the navigation does not change under the person's eyes on the next visit.
const modeKey = (accountId) => `printags:setup-mode:${ accountId }`;
export const readCachedMode = (accountId) => {
  try {
    const value = localStorage.getItem(modeKey(accountId));
    return value === "guided" || value === "full" ? value : null;
  } catch {
    return null;
  }
};
export const writeCachedMode = (accountId, mode) => {
  try {
    if (mode === "guided" || mode === "full") localStorage.setItem(modeKey(accountId), mode);
  } catch { /* private browsing: the navigation simply waits for the API */ }
};

// The navigation of each mode. Guided (and while unknown): the tools the steps send to, the team to bring in, nothing to
// read yet; the full dashboard trades the team (still in the account menu) and the documentation for the transactions
// and the invoices. An unknown mode shows the guided list: the next visit rarely changes it. The plan is not a page of
// the navigation: it lives in « Solde et forfait » (components/billing/PlanMenu.vue), with its state and the way out.
// `label`: the entry's name, `nav.<label>`.
const INVOICES = { url: "/settings/billing#invoices_history", label: "invoices", icon: "isax isax-bill" };
export const navigationFor = (mode, { docsUrl } = {}) => {
  const docs = { url: docsUrl, label: "docs", icon: "isax isax-document-text", external: true };
  if (mode === "none") {
    // `onboarding`: the entry stays highlighted on /onboarding, the getting-started page, which is not its URL.
    return [
      { url: "/dashboard", label: "start", icon: "isax isax-flash", onboarding: true },
      { url: "/developers", label: "developers", icon: "isax isax-code" },
      docs,
      INVOICES
    ];
  }
  if (mode === "full") {
    return [
      { url: "/dashboard", label: "home", icon: "isax isax-element-4" },
      { url: "/orders", label: "orders", icon: "isax isax-task-square" },
      { url: "/developers", label: "developers", icon: "isax isax-code" },
      { url: "/payments", label: "transactions", icon: "isax isax-repeat" },
      INVOICES
    ];
  }
  return [
    { url: "/dashboard", label: "gettingStarted", icon: "isax isax-flash", onboarding: true },
    { url: "/orders", label: "orders", icon: "isax isax-task-square" },
    { url: "/developers", label: "developers", icon: "isax isax-code" },
    { url: "/team", label: "team", icon: "isax isax-people" },
    docs
  ];
};

// What a question to the team can be about, as the help form offers it: anything (`help.topics.general`), or a step
// of the getting started (its title). The codes are the API's (services/accounts/helpRequests.js); `account` is never
// a question.
export const HELP_GENERAL = "general";
export const helpTopics = () => [
  { value: HELP_GENERAL, labelKey: "help.topics.general" },
  ...STEPS.filter((step) => step.key !== "account").map((step) => ({ value: step.key, labelKey: `onboarding.steps.${ step.key }.title` }))
];
export const helpTopicOf = (key) => (key && key !== "account" && STEPS.some((step) => step.key === key) ? key : HELP_GENERAL);

// The journey from sign-up to production, as one line (`onboarding.journey.<key>`): /welcome took the account through
// the first two stages, the getting started takes it through the last two.
export const JOURNEY = [{ key: "account" }, { key: "plan" }, { key: "integration" }, { key: "production" }];

// Each stage "done", "current" or "upcoming". The integration is the sandbox and the connection (phases `try` and
// `connect`); production is the `live` phase, or a first real order placed without going through every step.
export const journeyStages = (steps, { completedAt = null } = {}) => {
  const phases = Object.fromEntries(phaseProgress(steps).map((phase) => [phase.key, phase.complete]));
  const integrated = !!completedAt || (phases.try && phases.connect);
  const inProduction = !!completedAt || !!phases.live;
  const state = { account: "done", plan: "done", integration: integrated ? "done" : "current", production: inProduction ? "done" : integrated ? "current" : "upcoming" };
  return JOURNEY.map((stage) => ({ ...stage, state: state[stage.key] }));
};

// The dashboard's welcome, once: shown when this browser saw the account guided and now sees the full dashboard.
const readyKey = (accountId) => `printags:dashboard-ready:${ accountId }`;
export const readySeen = (accountId) => {
  try {
    return localStorage.getItem(readyKey(accountId)) === "seen";
  } catch {
    return true;
  }
};
export const markReadySeen = (accountId) => {
  try {
    localStorage.setItem(readyKey(accountId), "seen");
  } catch { /* the welcome simply shows again next time */ }
};

// Statuses of a test order before it is processed by the worker: `simulate` answers 409 while it is in one of them.
export const PENDING_STATUSES = ["creating", "draft", "in_queue"];

// Progress order of an order, to know which transitions are already behind it.
export const STATUS_RANK = [
  "validated", "processing", "treated", "batching", "sent_for_printing", "manufacturing",
  "packaging", "packaged", "ready_for_shipping", "shipped", "delivered"
];

// The visible lifecycle milestones offered for simulation, and the incidents to test.
export const SIMULATED_FLOW = ["processing", "manufacturing", "shipped", "delivered"];
export const SIMULATED_INCIDENTS = ["failed", "returned", "cancelled"];

export const isPendingStatus = (status) => PENDING_STATUSES.includes(status);

// The color of a status dot: pending, well advanced, in incident, or in progress. The label says it too.
export const statusTone = (status) => {
  if (isPendingStatus(status)) return "wait";
  if (["shipped", "delivered"].includes(status)) return "ok";
  if (["failed", "returned", "cancelled", "refunded"].includes(status)) return "bad";
  return "info";
};

// True when the order has already reached `target`: the matching button has no reason to stay active.
export const hasReached = (status, target) => {
  const current = STATUS_RANK.indexOf(status);
  return current !== -1 && current >= STATUS_RANK.indexOf(target);
};

// The order sent by the « Créer une commande de test » button and by the API examples.
// Real address (rue de la Paix, Paris 2e): the API geocodes it as for a real order.
// No visual and no chip: a test order does not need them.
export const sandboxOrder = () => ({
  shipTo: {
    name: "Camille Martin",
    email: "camille.martin@example.com",
    phone: "+33612345678",
    address: { addressLine1: "10 rue de la Paix", city: "Paris", postcode: "75002", countryCode: "FR" }
  },
  products: [{ modelId: "pvccard_white", quantity: 10 }]
});

const KEY_PLACEHOLDER = "$PRINTAGS_KEY";

// `key`: the test key just created, for an example ready to paste. Never a production key.
export const orderSnippets = (apiBase, key = null) => {
  const body = JSON.stringify(sandboxOrder(), null, 2);
  return {
    curl: [
      `curl -X POST ${ apiBase }/accounts/@me/orders \\`,
      `  -H "Authorization: Bearer ${ key ?? KEY_PLACEHOLDER }" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '${ body }'`
    ].join("\n"),
    node: [
      `const response = await fetch("${ apiBase }/accounts/@me/orders", {`,
      `  method: "POST",`,
      `  headers: {`,
      `    Authorization: ${ key ? `"Bearer ${ key }"` : "`Bearer ${ process.env.PRINTAGS_KEY }`" },`,
      `    "Content-Type": "application/json"`,
      `  },`,
      `  body: JSON.stringify(${ body.replace(/\n/g, "\n  ") })`,
      `});`,
      `const { orderId } = await response.json();`
    ].join("\n"),
    python: [
      `import ${ key ? "" : "os, " }requests`,
      ``,
      `response = requests.post(`,
      `    "${ apiBase }/accounts/@me/orders",`,
      `    headers={"Authorization": ${ key ? `"Bearer ${ key }"` : `f"Bearer {os.environ['PRINTAGS_KEY']}"` }},`,
      `    json=${ toPython(sandboxOrder(), 4, 2) },`,
      `)`,
      `order_id = response.json()["orderId"]`
    ].join("\n")
  };
};

export const SNIPPET_LANGUAGES = [
  { value: "curl", label: "cURL" },
  { value: "node", label: "Node.js" },
  { value: "python", label: "Python" }
];

export const simulateSnippet = (apiBase) => [
  `curl -X POST ${ apiBase }/accounts/@me/orders/<orderId>/simulate \\`,
  `  -H "Authorization: Bearer ${ KEY_PLACEHOLDER }" \\`,
  `  -H "Content-Type: application/json" \\`,
  `  -d '{ "status": "shipped" }'`
].join("\n");

// Same algorithm as the delivery on the API side (services/webhooks/delivery.services.js): `t=<seconds>,v1=<hmac>`,
// HMAC SHA-256 of `<t>.<raw body>` with the endpoint's whsec_ secret. `comments`: the three lines that explain the
// arguments, in the reader's language (`onboarding.snippet`).
export const webhookVerifySnippet = ({
  rawBody = "the body as received, before any JSON.parse.",
  header = "the value of X-Printags-Signature.",
  secret = "the whsec_... shown when the endpoint was created."
} = {}) => [
  `import crypto from "node:crypto";`,
  ``,
  `// rawBody: ${ rawBody }`,
  `// header: ${ header }`,
  `// secret: ${ secret }`,
  `export function verifyPrintags(rawBody, header, secret, toleranceSeconds = 300) {`,
  `  const parts = Object.fromEntries(header.split(",").map((part) => part.split("=")));`,
  `  if (Math.abs(Date.now() / 1000 - Number(parts.t)) > toleranceSeconds) return false;`,
  `  const expected = crypto.createHmac("sha256", secret).update(\`\${ parts.t }.\${ rawBody }\`).digest("hex");`,
  `  const a = Buffer.from(expected);`,
  `  const b = Buffer.from(parts.v1 ?? "");`,
  `  return a.length === b.length && crypto.timingSafeEqual(a, b);`,
  `}`
].join("\n");

// Serializes a JSON object into a readable Python literal (dict, list, str, int), indented by steps of `indent`.
function toPython(value, indent, depth = 1) {
  const pad = " ".repeat(indent * depth);
  const close = " ".repeat(indent * (depth - 1));
  if (Array.isArray(value)) {
    return `[\n${ value.map((item) => `${ pad }${ toPython(item, indent, depth + 1) },`).join("\n") }\n${ close }]`;
  }
  if (value && typeof value === "object") {
    return `{\n${ Object.entries(value).map(([k, v]) => `${ pad }${ JSON.stringify(k) }: ${ toPython(v, indent, depth + 1) },`).join("\n") }\n${ close }}`;
  }
  return JSON.stringify(value);
}
